import { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * Consistent breakpoints for the entire application
 * Mobile-first approach with clear boundaries
 */
export const BREAKPOINTS = {
  mobile: 640,      // Small phones and phones
  tablet: 768,      // Tablets and large phones  
  desktop: 1024,    // Small desktops and laptops
  desktopLg: 1280,  // Large desktops
  desktopXl: 1536,  // Extra large desktops
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;
export type ScreenSize = 'mobile' | 'tablet' | 'desktop' | 'desktopLg' | 'desktopXl';

interface Dimensions {
  width: number;
  height: number;
}

export interface UseResponsiveReturn {
  // Current screen size
  screenSize: ScreenSize;
  width: number;
  height: number;
  
  // Boolean helpers for common checks
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isDesktopLg: boolean;
  isDesktopXl: boolean;
  
  // Comparison helpers
  isAtLeast: (breakpoint: Breakpoint) => boolean;
  isAtMost: (breakpoint: Breakpoint) => boolean;
  isBetween: (min: Breakpoint, max: Breakpoint) => boolean;
  
  // Orientation
  isPortrait: boolean;
  isLandscape: boolean;
  orientation: 'portrait' | 'landscape';
  
  // Touch support
  isTouchDevice: boolean;
  
  // PWA mode
  isStandalone: boolean;
}

/**
 * Centralized responsive hook
 * Replaces all manual window.innerWidth checks
 * 
 * @example
 * const { isMobile, isDesktop, screenSize } = useResponsive();
 * 
 * if (isMobile) {
 *   return <MobileView />;
 * }
 * 
 * return <DesktopView />;
 */
export const useResponsive = (): UseResponsiveReturn => {
  // Initialize with current window dimensions (SSR-safe)
  const [dimensions, setDimensions] = useState<Dimensions>(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));
  
  useEffect(() => {
    // Debounced resize handler for performance
    let timeoutId: NodeJS.Timeout;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setDimensions({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, 150); // 150ms debounce - balances responsiveness and performance
    };
    
    // Listen for resize events
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Calculate screen size based on width
  const screenSize: ScreenSize = useMemo(() => {
    const { width } = dimensions;
    
    if (width < BREAKPOINTS.mobile) return 'mobile';
    if (width < BREAKPOINTS.tablet) return 'mobile';
    if (width < BREAKPOINTS.desktop) return 'tablet';
    if (width < BREAKPOINTS.desktopLg) return 'desktop';
    if (width < BREAKPOINTS.desktopXl) return 'desktopLg';
    return 'desktopXl';
  }, [dimensions.width]);
  
  // Boolean helpers for common checks
  const isMobile = screenSize === 'mobile';
  const isTablet = screenSize === 'tablet';
  const isDesktop = screenSize === 'desktop' || screenSize === 'desktopLg' || screenSize === 'desktopXl';
  const isDesktopLg = screenSize === 'desktopLg';
  const isDesktopXl = screenSize === 'desktopXl';
  
  // Comparison helpers
  const isAtLeast = useCallback((breakpoint: Breakpoint): boolean => {
    return dimensions.width >= BREAKPOINTS[breakpoint];
  }, [dimensions.width]);
  
  const isAtMost = useCallback((breakpoint: Breakpoint): boolean => {
    return dimensions.width <= BREAKPOINTS[breakpoint];
  }, [dimensions.width]);
  
  const isBetween = useCallback((min: Breakpoint, max: Breakpoint): boolean => {
    return dimensions.width >= BREAKPOINTS[min] && dimensions.width <= BREAKPOINTS[max];
  }, [dimensions.width]);
  
  // Orientation
  const isPortrait = dimensions.height > dimensions.width;
  const isLandscape = dimensions.width > dimensions.height;
  const orientation: 'portrait' | 'landscape' = isPortrait ? 'portrait' : 'landscape';
  
  // Touch support detection
  const isTouchDevice = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);
  
  // PWA standalone mode detection
  const isStandalone = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches;
  }, []);
  
  return {
    screenSize,
    width: dimensions.width,
    height: dimensions.height,
    isMobile,
    isTablet,
    isDesktop,
    isDesktopLg,
    isDesktopXl,
    isAtLeast,
    isAtMost,
    isBetween,
    isPortrait,
    isLandscape,
    orientation,
    isTouchDevice,
    isStandalone,
  };
};

/**
 * Simple media query hook for specific queries
 * Use this for one-off media queries that don't fit the standard breakpoints
 * 
 * @example
 * const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
 * const isDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });
  
  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    
    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
    
    // Fallback for older browsers
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, [query]);
  
  return matches;
};

/**
 * Get responsive value based on current screen size
 * Useful for conditional values that change with screen size
 * 
 * @example
 * const columns = useResponsiveValue({
 *   mobile: 1,
 *   tablet: 2,
 *   desktop: 3,
 *   desktopLg: 4,
 * });
 */
export const useResponsiveValue = <T,>(values: Partial<Record<ScreenSize, T>>): T | undefined => {
  const { screenSize } = useResponsive();
  
  return useMemo(() => {
    // Try exact match first
    if (values[screenSize] !== undefined) {
      return values[screenSize];
    }
    
    // Fallback logic: try smaller sizes
    const fallbackOrder: ScreenSize[] = ['desktopXl', 'desktopLg', 'desktop', 'tablet', 'mobile'];
    const currentIndex = fallbackOrder.indexOf(screenSize);
    
    for (let i = currentIndex + 1; i < fallbackOrder.length; i++) {
      const size = fallbackOrder[i];
      if (values[size] !== undefined) {
        return values[size];
      }
    }
    
    return undefined;
  }, [screenSize, values]);
};
